import { useState } from 'react';
import * as Collapsible from '@radix-ui/react-collapsible';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useCollectionBinding,
  useSlotGroup,
  usePropertyAddressUrl,
  SectionContextProvider,
  EditableCollectionBinding,
  EditableSlotGroup,
  EditableToken,
  RichTextContent,
} from '@vibesites/runtime/react';
import { blurbDefault, clean, formatPhone, hoursRows, stagger, telHref, type OfficeHour } from '../utils/quinn';
import { useIsEditing, useRawProperty } from '../utils/hooks';
import { tourCta, useSiteFlags } from '../utils/site';

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book a Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const callCtaDef: SlotGroupDefinition = {
  label: 'Call Button',
  description: 'Hidden when there is no phone number. Formatted as (555) 555-5555 automatically.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Call' },
    number: {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'property_call_tracking_phone',
      suggestedFields: ['property_call_tracking_phone', 'property_phone'],
    },
  },
};

export function VisitUs() {
  const p = useRawProperty();
  const flags = useSiteFlags();
  const editing = useIsEditing();
  const directionsUrl = usePropertyAddressUrl();
  const tour = useSlotGroup('visitus.tour_cta', tourCtaDef);
  const call = useSlotGroup('visitus.call_cta', callCtaDef);
  const hours = useCollectionBinding<OfficeHour>('visitus.hours', { collection: 'office_hours' });
  const formSlot = useBlockSlot('visitus.form_block', { defaultValue: 'multihub-lead-form' });
  const [formOpen, setFormOpen] = useState(false);

  const titleDefault = blurbDefault(p.property_scheduletour_blurb, 'title');
  const tourLink = tourCta(tour, flags.selfTourUrl);
  const phone = formatPhone(call.number) || flags.phone;
  const tel = telHref(phone);
  const rows = hoursRows(hours, !!flags.selfTourUrl);
  const city = clean(p.property_address_city);
  const state = clean(p.property_address_state);
  const open = formOpen || editing;

  return (
    <section
      className="section section--surface flex items-stretch justify-between gap-16 border-t border-t-[--q-border] max-[1199px]:flex-col max-[1199px]:gap-12"
      id="contact"
    >
      <div className="flex min-w-0 flex-[0_1_592px] flex-col gap-7 max-[1199px]:flex-auto" data-reveal>
        <RichTextContent path="visitus.eyebrow" as="div" className="eyebrow" />
        <RichTextContent path="visitus.title" as="h2" className="h1" defaultValue={titleDefault} />
        <RichTextContent path="visitus.lead" as="p" className="m-0 text-lg leading-7 text-[--q-muted]" />
        <div className="flex flex-wrap gap-3 pt-1 max-[767px]:flex-col">
          <EditableSlotGroup groupPath="visitus.tour_cta" definition={tourCtaDef}>
            <a
              className="btn btn-primary"
              href={tourLink.href}
              target={tourLink.isTour ? tour._target || '_self' : '_self'}
              data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
            >
              {tourLink.label}
            </a>
          </EditableSlotGroup>
          <EditableSlotGroup groupPath="visitus.call_cta" definition={callCtaDef}>
            {tel && (
              <a className="btn btn-secondary dniphonehref" href={tel}>
                {call.label || 'Call'} <span className="dniphone">{phone}</span>
              </a>
            )}
          </EditableSlotGroup>
        </div>
        <div className="office">
          <div className="office__col">
            <div className="label">Leasing office</div>
            <a className="text-inherit no-underline hover:underline hover:underline-offset-[3px]" href={directionsUrl} target="_blank" rel="noopener">
              {clean(p.property_address_1) && <div>{clean(p.property_address_1)}</div>}
              {(city || state) && (
                <div>
                  {[city, state].filter(Boolean).join(', ')} {clean(p.property_address_zip) || ''}
                </div>
              )}
            </a>
            <a className="textlink" href={directionsUrl} target="_blank" rel="noopener">Get Directions</a>
          </div>
          <div className="office__col">
            <div className="label">Hours</div>
            <EditableCollectionBinding bindingPath="visitus.hours" label="Office Hours">
              <div className="hours">
                {rows.length ? (
                  rows.map(([d, t]) => (
                    <div className="hours__row" key={d}>
                      <span>{d}</span>
                      <span>{t}</span>
                    </div>
                  ))
                ) : (
                  <div className="hours__row">
                    <a
                      className="textlink"
                      href={tourLink.href}
                      target={tourLink.isTour ? tour._target || '_self' : '_self'}
                      data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
                    >
                      {tourLink.label}
                    </a>
                  </div>
                )}
              </div>
            </EditableCollectionBinding>
          </div>
        </div>
      </div>

      <Collapsible.Root open={open} onOpenChange={setFormOpen} className="form visit__form min-w-0 min-[1200px]:min-w-[480px]" data-reveal style={stagger(1)}>
        <div className="form__head">
          <RichTextContent path="visitus.form_title" as="h3" className="h4" />
          <RichTextContent path="visitus.form_subtitle" as="div" className="form__sub" />
        </div>
        {!open && (
          <Collapsible.Trigger className="btn btn-primary self-start" aria-controls="visit-form">
            <RichTextContent path="visitus.form_toggle" as="span" />
            <svg className="h-3 w-3 fill-none stroke-current stroke-[1.5] [stroke-linecap:round] [stroke-linejoin:round]" viewBox="0 0 12 12" aria-hidden="true"><path d="M2 4.5 6 8.5l4-4" /></svg>
          </Collapsible.Trigger>
        )}
        <div id="visit-form" className="visit__form-body" hidden={!open}>
          <EditableToken path="visitus.form_block" as="div">
            {formSlot ? (
              <SectionContextProvider sectionId={formSlot.sectionId}>
                <formSlot.BlockComponent sectionId={formSlot.sectionId} />
              </SectionContextProvider>
            ) : (
              <div className="form__sub">No form selected</div>
            )}
          </EditableToken>
        </div>
      </Collapsible.Root>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VisitUs',
  displayName: 'Visit Us',
  tokens: {
    'visitus.eyebrow': { label: 'Eyebrow', type: 'richtext', defaultValue: '<p>Visit us</p>' },
    'visitus.title': {
      label: 'Title',
      description: 'Until edited, shows part of the blurb (its bold first sentence for a title, the rest for a lead). Pick the blurb field from the suggestions to show it in full.',
      type: 'richtext',
      defaultValue: '<p>Come see it in person</p>',
      suggestedFields: ['property_scheduletour_blurb', 'property_contactus_blurb'],
    },
    'visitus.lead': {
      label: 'Lead',
      type: 'richtext',
      defaultValue: '<p>Book a self-guided tour on your schedule, or send the leasing team a note.</p>',
    },
    'visitus.form_title': { label: 'Form Title', type: 'richtext', defaultValue: '<p>Send the leasing team a note</p>' },
    'visitus.form_subtitle': { label: 'Form Subtitle', type: 'richtext', defaultValue: '<p>We reply within one business day.</p>' },
    'visitus.form_toggle': { label: 'Open Form Button', type: 'richtext', defaultValue: '<p>Contact Us</p>' },
    'visitus.form_block': {
      label: 'Contact Form',
      type: 'block',
      defaultValue: 'multihub-lead-form',
      acceptedBlockCategories: ['Forms'],
    },
  },
  slotGroups: {
    'visitus.tour_cta': tourCtaDef,
    'visitus.call_cta': callCtaDef,
  },
  collectionBindings: {
    'visitus.hours': {
      label: 'Office Hours',
      collection: 'office_hours',
    },
  },
};

(VisitUs as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default VisitUs;
